/* eslint-disable */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { useTheme } from '@mui/material/styles';
import escapeHtml from 'escape-html';
import isNumber from 'lodash/isNumber';
import isNaN from 'lodash/isNaN';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import each from 'lodash/each';
import includes from 'lodash/includes';
import join from 'lodash/join';
import map from 'lodash/map';
import min from 'lodash/min';
import max from 'lodash/max';
import moment from 'moment';

import NumberFormat from 'react-number-format';
import { CardElement } from '@stripe/react-stripe-js';
import MaskedInput from 'react-text-mask';
import { createEditor, Text } from 'slate';
import { Editable, Slate, withReact } from 'slate-react';
import { v4 as uuidv4 } from 'uuid';
import { enqueueSnackbar } from 'notistack';
import { X as XIcon } from 'react-feather';
import { offsetToTimeString, StopProp } from '../utils';
import { usePublicConfig } from '../providers';

const styles = {
  timeField: (theme) => ({
    color: theme.palette.text.secondary,
  }),
  hiddenFileInput: {
    position: 'fixed',
    top: '-100em',
  },
  centerElement: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },
  centerContainer: {
    position: 'relative',
    width: '100%',
    height: '100%',
  },
  rteWrapper: {
    borderStyle: 'solid',
    borderRadius: 4,
    margin: 2,
  },
  multipleFormControl: {
    width: '100%',
  },
};

export const PvTextField = ({
  value,
  shrink = false,
  children = null,
  forceError = false,
  forceValidate = false,
  variant = 'outlined',
  disableUnderline = false,
  inputProps = {},
  InputProps: parentInputProps = {},
  ...props // TODO need to remove prop spreading
}) => {
  const [error, setError] = useState(props.error || forceError);
  const [hover, setHover] = useState(false);

  useEffect(() => {
    setError(forceValidate && props.required && !value);
  }, [value, forceValidate, props.required]);

  const shouldDisableUnderline = disableUnderline && variant !== 'outlined';

  const InputProps = useMemo(() => {
    const baseProps = shouldDisableUnderline
      ? {
          disableUnderline: disableUnderline || !hover,
        }
      : {};
    return {
      ...baseProps,
      ...parentInputProps,
    };
  }, [shouldDisableUnderline, disableUnderline, hover, parentInputProps]);

  return (
    <TextField
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      fullWidth
      variant={variant}
      value={value}
      error={!!error}
      {...props}
      inputProps={inputProps}
      InputProps={InputProps}
    >
      {children}
    </TextField>
  );
};

const createCurrencyMask = ({
  fixedDecimalScale = true,
  decimalScale = 2,
  allowNegative = false,
}) =>
  React.forwardRef((props, ref) => {
    const { inputRef, onChange, ...other } = props;

    return (
      <NumberFormat
        {...other}
        getInputRef={inputRef || ref}
        onValueChange={(values) => {
          onChange({
            target: {
              name: props.name,
              value: values.value,
            },
          });
        }}
        fixedDecimalScale={fixedDecimalScale}
        decimalScale={decimalScale}
        allowNegative={allowNegative}
        thousandSeparator
        prefix="$"
      />
    );
  });

export const useCurrencyState = (initialValue) => {
  const v =
    parseInt(initialValue) == undefined ? '' : parseInt(initialValue) / 100.0;
  return useState(v);
};

export const PvCurrencyMaskField = ({
  showCents = true,
  allowNegative = false,
  InputProps: parentInputProps = {},
  ...props
}) => {
  const decimalScale = showCents ? 2 : 0;
  const currencyMask = useMemo(
    () => createCurrencyMask({ decimalScale, allowNegative }),
    [decimalScale, allowNegative]
  );

  const InputProps = useMemo(
    () => ({
      ...parentInputProps,
      inputComponent: currencyMask,
    }),
    [parentInputProps, currencyMask]
  );

  const shrink = isNumber(props.value) || props.shrink;

  return <PvTextField {...props} shrink={shrink} InputProps={InputProps} />;
};

export const PhoneMask = React.forwardRef((props, ref) => {
  const { inputRef, ...other } = props;
  const maskedInputRef = React.useRef(null);

  React.useImperativeHandle(ref, () => ({
    focus: () => {
      if (maskedInputRef.current && maskedInputRef.current.inputElement) {
        maskedInputRef.current.inputElement.focus();
      }
    },
    get value() {
      return maskedInputRef.current?.inputElement?.value;
    },
  }));

  return (
    <MaskedInput
      {...other}
      ref={(maskedRef) => {
        maskedInputRef.current = maskedRef;
        if (typeof inputRef === 'function') {
          inputRef(maskedRef ? maskedRef.inputElement : null);
        }
      }}
      mask={[
        '(',
        /[1-9]/,
        /\d/,
        /\d/,
        ')',
        ' ',
        /\d/,
        /\d/,
        /\d/,
        '-',
        /\d/,
        /\d/,
        /\d/,
        /\d/,
      ]}
    />
  );
});

export const PvPhoneField = ({
  InputProps: parentInputProps = {},
  ...props
}) => {
  const InputProps = useMemo(
    () => ({
      ...parentInputProps,
      inputComponent: PhoneMask,
    }),
    [parentInputProps]
  );
  return <PvTextField type="tel" {...props} InputProps={InputProps} />;
};

const BusinessTaxIdMask = React.forwardRef((props, ref) => {
  const { inputRef, ...other } = props;

  return (
    <MaskedInput
      {...other}
      ref={(maskedRef) => {
        if (typeof inputRef === 'function') {
          inputRef(maskedRef ? maskedRef.inputElement : null);
        }
        if (typeof ref === 'function') {
          ref(maskedRef);
        } else if (ref) {
          ref.current = maskedRef;
        }
      }}
      mask={[/\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/]}
    />
  );
});

export const PvBusinessTaxIdField = ({
  InputProps: parentInputProps = {},
  ...props
}) => {
  const InputProps = useMemo(
    () => ({
      ...parentInputProps,
      inputComponent: BusinessTaxIdMask,
    }),
    [parentInputProps]
  );
  return <PvTextField {...props} InputProps={InputProps} />;
};

const SocialSecurityNumberMask = React.forwardRef((props, ref) => {
  const { inputRef, ...other } = props;

  return (
    <MaskedInput
      {...other}
      ref={(maskedRef) => {
        if (typeof inputRef === 'function') {
          inputRef(maskedRef ? maskedRef.inputElement : null);
        }
        if (typeof ref === 'function') {
          ref(maskedRef);
        } else if (ref) {
          ref.current = maskedRef;
        }
      }}
      mask={[/\d/, /\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
    />
  );
});

export const PvSocialSecurityNumberField = ({
  InputProps: parentInputProps = {},
  ...props
}) => {
  const InputProps = useMemo(
    () => ({
      ...parentInputProps,
      inputComponent: SocialSecurityNumberMask,
    }),
    [parentInputProps]
  );
  return <PvTextField {...props} InputProps={InputProps} />;
};

const createPercentageMask = ({ decimalScale }) =>
  React.forwardRef((props, ref) => {
    const { inputRef, onChange, ...other } = props;

    return (
      <NumberFormat
        {...other}
        getInputRef={inputRef || ref}
        allowNegative={false}
        onValueChange={(values) => {
          onChange({
            target: {
              value: values.value,
            },
          });
        }}
        decimalScale={decimalScale}
      />
    );
  });

export const usePercentageState = (val) => {
  const v = parseInt(val) == undefined ? '' : val;
  return useState(v);
};

export const PvPercentageField = ({
  decimalScale,
  InputProps: parentInputProps = {},
  inputProps: parentInputProps2 = {},
  ...props
}) => {
  const percentageMask = useMemo(
    () => createPercentageMask({ decimalScale }),
    [decimalScale]
  );

  const endAdornment = useMemo(
    () => <InputAdornment position="end">%</InputAdornment>,
    []
  );

  const InputProps = useMemo(
    () => ({
      ...parentInputProps,
      inputComponent: percentageMask,
      endAdornment,
    }),
    [parentInputProps, percentageMask, endAdornment]
  );

  const inputProps = useMemo(
    () => ({
      ...parentInputProps2,
      style: { textAlign: 'right' },
    }),
    [parentInputProps2]
  );

  return (
    <PvTextField {...props} inputProps={inputProps} InputProps={InputProps} />
  );
};

const createIntegerMask = ({ allowNegative = true }) =>
  React.forwardRef(({ inputRef, onChange, ...props }, ref) => (
    <NumberFormat
      {...props}
      getInputRef={inputRef || ref}
      onValueChange={(values) => {
        onChange({
          target: {
            value: parseInt(values.value),
          },
        });
      }}
      allowNegative={allowNegative}
    />
  ));

export const PvIntegerField = ({
  allowNegative,
  inputProps = {},
  InputProps: parentInputProps = {},
  ...props
}) => {
  const integerMask = useMemo(
    () => createIntegerMask({ allowNegative }),
    [allowNegative]
  );

  const InputProps = useMemo(
    () => ({
      ...parentInputProps,
      inputComponent: integerMask,
    }),
    [parentInputProps, integerMask]
  );

  return (
    <PvTextField {...props} inputProps={inputProps} InputProps={InputProps} />
  );
};

export const PvPasswordField = (props) => {
  const [showPassword, setShowPassword] = useState(false);

  const icon = showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />;
  const type = showPassword ? 'text' : 'password';
  const tooltipText = showPassword ? 'Hide Password' : 'Show Password';

  const endAdornment = (
    <InputAdornment position="end">
      <Tooltip title={tooltipText}>
        <IconButton edge="end" onClick={() => setShowPassword(!showPassword)}>
          {icon}
        </IconButton>
      </Tooltip>
    </InputAdornment>
  );

  return (
    <PvTextField
      type={type}
      inputProps={{ ...props.inputProps, spellCheck: false }}
      InputProps={{ endAdornment }}
      {...props}
    />
  );
};

export const PvMultiSelect = ({
  label,
  value,
  onChange,
  vals,
  renderValue,
  size = 'medium',
  variant = 'outlined',
  emptyText = '',
  placeholder,
  makeOption,
  shrink,
  forceValidate,
  helperText,
  ...props
}) => {
  const labelId = useRef(uuidv4());
  const [error, setError] = useState(false);
  const inputProps = {
    label,
  };

  useEffect(() => {
    setError(forceValidate && props.required && isEmpty(value));
  }, [value, forceValidate, props.required]);

  const defaultMakeOption = (option) => {
    const v = isArray(option) ? option[0] : option;
    const title = isArray(option) ? option[1] : option;
    const disabled = isArray(option) ? option[2] : false;
    const style = isArray(option) ? { ...(option[3] || []) } : {};
    return (
      <MenuItem
        key={v}
        value={v}
        disabled={disabled}
        style={style}
        data-cy="dropdown-item"
        role="option"
      >
        <Checkbox
          color="primary"
          checked={!!(value && value.indexOf(v) > -1)}
        />
        <Typography>{title}</Typography>
      </MenuItem>
    );
  };

  const defaultRenderValue = (selected) => {
    if (isEmpty(selected)) {
      return emptyText;
    }
    const labels = [];
    each(vals, (val) => {
      const id = isArray(val) ? val[0] : val;
      if (includes(selected, id)) {
        labels.push(isArray(val) ? val[1] : val);
      }
    });
    return join(labels, ', ');
  };

  const isInIframe = window !== window.parent;
  const MenuProps = {
    disablePortal: isInIframe,
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'left',
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'left',
    },
    PaperProps: {
      style: {
        maxHeight: 200,
        marginTop: 4,
      },
    },
    MenuListProps: {
      style: {
        paddingTop: 0,
        paddingBottom: 0,
      },
    },
  };

  const mOption = makeOption || defaultMakeOption;
  return (
    <FormControl size={size} variant={variant} fullWidth error={error}>
      <InputLabel
        id={labelId.current}
        disabled={props.disabled}
        required={props.required}
        variant={variant}
      >
        {label}
      </InputLabel>
      <Select
        error={error}
        MenuProps={MenuProps}
        labelId={labelId.current}
        label={label}
        multiple
        value={value || []}
        onChange={onChange}
        variant={variant}
        renderValue={renderValue || defaultRenderValue}
        {...inputProps}
        {...props}
      >
        {map(vals, (val) => mOption(val))}
      </Select>
      {helperText && <FormHelperText error={error}>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export const PvDropDown = ({
  vals,
  className = '',
  SelectProps = {},
  value,
  selectTestId = '',
  inputProps = {},
  sx = {},
  ...rest
}) => {
  const makeOption = (option, mobile = false) => {
    const v = Array.isArray(option) ? option[0] : option;
    const title = Array.isArray(option) ? option[1] : option;
    const disabled = Array.isArray(option) ? option[2] : false;
    const optionKey = `option-${rest.name}-${v}`;
    return mobile ? (
      <option disabled={disabled} key={optionKey} value={v}>
        {title}
      </option>
    ) : (
      <MenuItem
        disabled={disabled}
        key={optionKey}
        value={v}
        data-cy="dropdown-item"
        role="option"
      >
        {title}
      </MenuItem>
    );
  };

  const isInIframe = window !== window.parent;
  const MenuProps = {
    disablePortal: isInIframe,
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'left',
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'left',
    },
    PaperProps: {
      style: {
        maxHeight: 200,
        marginTop: 4,
      },
    },
    MenuListProps: {
      style: {
        paddingTop: 0,
        paddingBottom: 0,
      },
    },
  };

  const filteredVals = vals.filter((option) => {
    const v = Array.isArray(option) ? option[0] : option;
    const title = Array.isArray(option) ? option[1] : option;
    return v !== '' || (title && title.toString().trim().length > 0);
  });

  const availableValues = filteredVals.map((option) =>
    Array.isArray(option) ? option[0] : option
  );
  const isValueAvailable =
    value === '' ||
    value === null ||
    value === undefined ||
    availableValues.includes(value);
  const safeValue = isValueAvailable ? value : '';

  const selectId = rest.name ? `mui-component-select-${rest.name}` : undefined;

  return (
    <PvTextField
      select
      SelectProps={{ MenuProps, ...SelectProps, className, sx }}
      value={safeValue}
      data-cy={selectTestId}
      inputProps={{ ...inputProps, id: selectId }}
      {...rest}
    >
      {filteredVals.map((option) => makeOption(option))}
    </PvTextField>
  );
};

export const PvDateField = ({
  clearable,
  label,
  variant = 'outlined',
  formatString = 'MMM D, YYYY',
  value,
  forceValidate,
  testId,
  disablePast = false,
  disableFuture = false,
  inputProps = {},
  icon = <XIcon />,
  helperText,
  ...props
}) => {
  const isInitialMount = useRef(true);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setError(forceValidate && props.required && !value);
  }, [value, forceValidate, props.required]);

  const defaultLabelFunc = (date, _invalidLabel) => {
    if (!value) {
      return '';
    }
    const diff = moment(date)
      .startOf('day')
      .diff(moment().startOf('day'), 'days');
    if (diff === 0) {
      return 'Today';
    }
    if (diff === 1) {
      return 'Tomorrow';
    }
    return moment(date).format(formatString);
  };

  let InputProps = props.InputProps || {};

  if (clearable && value) {
    const endAdornment = (
      <InputAdornment position="end">
        <Tooltip title="Clear Date">
          <StopProp>
            <IconButton
              disabled={props.disabled}
              className="remove-date-button"
              edge="end"
              onClick={() => props.onChange(null)}
            >
              {icon}
            </IconButton>
          </StopProp>
        </Tooltip>
      </InputAdornment>
    );

    InputProps = {
      ...props.InputProps,
      endAdornment,
    };
  }

  const momentValue = value ? moment(value) : null;

  return (
    <DatePicker
      value={momentValue}
      format={formatString}
      label={label}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      slotProps={{
        textField: {
          variant,
          error,
          required: props.required,
          helperText,
          InputProps,
          inputProps: {
            ...inputProps,
            readOnly: true,
          },
          'data-cy': testId,
          style: { marginTop: 0 },
          fullWidth: true,
          size: props.size || 'medium',
          onClick: () => setOpen(true),
          onKeyDown: (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setOpen(true);
            }
          },
        },
        openPickerButton: {
          'data-cy': testId ? `${testId}-button` : undefined,
        },
        popper: {
          modifiers: [
            {
              name: 'preventOverflow',
              enabled: true,
              options: {
                boundary: 'viewport',
                altAxis: true,
                padding: 8,
              },
            },
          ],
          sx: {
            '& .MuiPaper-root': {
              overflowX: 'scroll',
              marginTop: '4px',
            },
          },
        },
        calendarHeader: {
          slotProps: {
            previousIconButton: { 'data-cy': 'datepicker-dec-month-button' },
            nextIconButton: { 'data-cy': 'datepicker-inc-month-button' },
          },
        },
      }}
      disablePast={disablePast}
      disableFuture={disableFuture}
      {...props}
    />
  );
};

export const PvTimeField = ({
  placeholder,
  start = 60,
  end = 1800,
  disabledTimes = {},
  interval = 15,
  value,
  inputProps = {},
  allowNone = false,
  ...rest
}) => {
  const theme = useTheme();
  const vals = [];

  if (placeholder || !allowNone) {
    vals.push(['', placeholder || '']);
  }

  if (allowNone) {
    vals.push(['NONE', 'None']);
  }

  // Bounds are collected by hand rather than through compact, which drops a
  // start of midnight because 0 is falsy and leaves the list beginning at
  // whatever the field already holds.
  const selected = value === 'NONE' || !isNumber(value) ? [] : [value];
  const bound = (limit) => (isNumber(limit) ? [limit] : []);
  const startOrExpanded = min([...bound(start), ...selected]);
  const endOrExpanded = max([...bound(end), ...selected]);
  for (let i = startOrExpanded; i <= endOrExpanded; i += interval) {
    vals.push([i, offsetToTimeString(i), disabledTimes[i]]);
  }

  const timeFieldSx =
    value === '' || value === null ? styles.timeField(theme) : undefined;

  const props = { vals, placeholder, ...rest };

  return (
    <PvDropDown
      value={value === null ? 'NONE' : value}
      sx={timeFieldSx}
      inputProps={inputProps}
      {...props}
    />
  );
};

export const PvImageInput = ({
  editOnHover = false,
  editing = false,
  value,
  defaultImage,
  onChange,
  label,
  maxSize = 10,
  helperText = '',
}) => {
  const { publicConfig } = usePublicConfig();
  const theme = useTheme();
  const elementId = useRef(uuidv4());
  const inputRef = useRef(null);
  const [hover, setHover] = useState(false);

  const src = value || defaultImage || publicConfig.images.defaultImage;

  const editMode = (editOnHover && hover) || editing;

  const handleChange = useCallback(
    (e) => {
      const file = e.target.files[0];
      const fileSize = file.size / 1024 / 1024; // in megabytes

      if (fileSize > maxSize) {
        enqueueSnackbar('Image is larger than 10mb', { variant: 'error' });
      } else {
        onChange(e);
      }
    },
    [maxSize, onChange]
  );

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <InputLabel style={{ marginBottom: theme.spacing(1), opacity: 1 }}>
        {label}
      </InputLabel>
      <div style={styles.centerContainer}>
        <img
          style={{
            opacity: editMode ? 0.5 : 1,
            border: '1px solid #0000001A',
            borderRadius: '6px',
          }}
          width="100%"
          height="auto"
          src={src}
          alt={label}
        />
        <div style={styles.centerElement}>
          <div>
            <label
              htmlFor={elementId.current}
              onFocus={() => setHover(true)}
              onBlur={() => setHover(false)}
              style={{
                opacity: editMode ? 1 : 0,
                '&:focus': {
                  opacity: 1,
                  visibility: 'visible',
                },
              }}
            >
              <Button
                variant="contained"
                size="small"
                component="span"
                onClick={(event) => {
                  event.preventDefault();
                  inputRef.current.click();
                }}
              >
                Choose Image
              </Button>
            </label>
          </div>
        </div>
      </div>
      <input
        id={elementId.current}
        onChange={handleChange}
        type="file"
        tabIndex={-1}
        style={styles.hiddenFileInput}
        ref={inputRef}
        accept="image/png,image/gif,image/jpeg,image/webp"
      />
      {helperText && (
        <Typography variant="body2" color="text.tertiary">
          {helperText}
        </Typography>
      )}
    </div>
  );
};

export const PvRichTextField = ({
  value,
  onChange,
  className,
  minEditorHeight,
  editing = true,
}) => {
  const theme = useTheme();
  const editor = useMemo(() => withReact(createEditor()), []);
  const [focus, setFocus] = useState(true);
  const borderColor =
    focus && editing ? theme.palette.primary.main : 'rgba(0,0,0,0.15)';
  const borderWidth = focus && editing ? 2 : 1;
  const padding = theme.spacing(2);

  if (editing) {
    return (
      <div
        className={className}
        style={{
          ...styles.rteWrapper,
          minHeight: minEditorHeight,
          borderColor,
          borderWidth,
          padding,
        }}
      >
        <Typography style={{ minHeight: minEditorHeight }} component="div">
          <Slate editor={editor} value={value} onChange={onChange}>
            <Editable
              style={{ minHeight: minEditorHeight }}
              spellCheck
              autoFocus
              onFocus={() => setFocus(true)}
              onBlur={() => setFocus(false)}
            />
          </Slate>
        </Typography>
      </div>
    );
  }
  return (
    <div
      className={className}
      style={{
        ...styles.rteWrapper,
        height: minEditorHeight,
        borderColor,
        borderWidth,
        padding,
      }}
    >
      <span dangerouslySetInnerHTML={{ __html: serializeToHtml(value) }} />
    </div>
  );
};

export const serializeToHtml = (node) => {
  if (Text.isText(node)) {
    return escapeHtml(node.text);
  }
  let children = isArray(node) ? node : Array.from(node.children);
  children = map(children, serializeToHtml).join('');
  switch (node.type) {
    case 'paragraph':
      return `<p>${children}</p>`;
    default:
      return children;
  }
};

export const emptyRichText = [{ type: 'paragraph', children: [{ text: '' }] }];

export const PvStripeField = ({
  buttonText = '',
  onButtonClick = () => {},
}) => {
  const theme = useTheme();

  const padding = buttonText ? 12 : 18;
  return (
    <div
      id="stripe-wrapper"
      data-cy="stripe-wrapper"
      style={{
        display: 'flex',
        alignItems: 'center',
        borderRadius: 4,
        border: '1px solid #ccc',
        padding,
      }}
    >
      <div style={{ flexGrow: 1 }}>
        <CardElement />
      </div>
      {buttonText && (
        <div style={{ flexShrink: 1 }}>
          <Button onClick={onButtonClick} size="small">
            {buttonText}
          </Button>
        </div>
      )}
    </div>
  );
};

export const PvCustomField = ({
  userFieldDefinition,
  onChange,
  label,
  value,
  variant = 'outlined',
  inputProps = {},
  ...props
}) => {
  const theme = useTheme();
  const { dataType, options, userFieldKey } = userFieldDefinition;

  switch (dataType) {
    case 'short_text':
      return (
        <PvTextField
          name={userFieldKey}
          label={label}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          variant={variant}
          inputProps={inputProps}
          {...props}
        />
      );
    case 'long_text':
      return (
        <PvTextField
          name={userFieldKey}
          label={label}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          variant={variant}
          multiline
          maxRows={12}
          minRows={2}
          inputProps={inputProps}
          {...props}
        />
      );
    case 'enum':
      const enumSx = !value ? styles.timeField(theme) : undefined;
      const vals = [...options];
      vals.unshift(['', '', props.required]);
      return (
        <PvDropDown
          name={userFieldKey}
          sx={enumSx}
          label={label}
          value={value || ''}
          vals={[...vals]}
          onChange={(e) => onChange(e.target.value)}
          variant={variant}
          inputProps={inputProps}
          {...props}
        />
      );
    case 'multi_enum':
      return (
        <PvMultiSelect
          name={userFieldKey}
          label={label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          variant={variant}
          vals={options}
          {...props}
        />
      );
    case 'phone':
      return (
        <PvPhoneField
          name={userFieldKey}
          label={label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          variant={variant}
          inputProps={inputProps}
          {...props}
        />
      );
    case 'date':
      return (
        <PvDateField
          name={userFieldKey}
          variant={variant}
          fullWidth
          value={value}
          onChange={(d) => onChange(d)}
          label={label}
          inputProps={inputProps}
          {...props}
        />
      );
    case 'time':
      return (
        <PvTimeField
          name={userFieldKey}
          fullWidth
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          label={label}
          variant={variant}
          inputProps={inputProps}
          data-cy="start-time-input"
          {...props}
        />
      );
    case 'currency':
      return (
        <PvCurrencyMaskField
          name={userFieldKey}
          fullWidth
          value={value}
          onChange={(e) => onChange(e.target.value)}
          label={label}
          variant={variant}
          {...props}
        />
      );
    case 'integer':
      return (
        <PvIntegerField
          name={userFieldKey}
          fullWidth
          value={value}
          onChange={(e) => onChange(e.target.value)}
          label={label}
          variant={variant}
          inputProps={inputProps}
          {...props}
        />
      );
    default:
      return null;
  }
};
